import { QueryClient, type QueryClientConfig } from '@tanstack/react-query';

export const queryClientDefaultOptions = {
  queries: {
    // 30 seconds stale time - ensures fresh data when navigating between pages
    staleTime: 30 * 1000,
    // 24 hours cache time
    gcTime: 24 * 60 * 60 * 1000,
    // Preserve freshness for quote-sensitive shipment/finalization flows
    refetchOnWindowFocus: true,
    // Refetch on reconnect
    refetchOnReconnect: true,
    // Retry configuration
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  },
  mutations: {
    // Retry once for mutations
    retry: 1,
  },
} satisfies QueryClientConfig['defaultOptions'];

export const queryClient = new QueryClient({
  defaultOptions: queryClientDefaultOptions,
});

/**
 * Query key factory for consistent key structure
 * Pattern: ['resource', storeId, ...filters]
 */
export const queryKeys = {
  account: {
    all: ['account'] as const,
    me: () => [...queryKeys.account.all, 'me'] as const,
    members: () => [...queryKeys.account.all, 'members'] as const,
    userMetrics: (filters?: Record<string, unknown>) =>
      [...queryKeys.account.all, 'user-metrics', filters] as const,
    roles: () => [...queryKeys.account.all, 'roles'] as const,
  },

  userPreferences: {
    all: ['userPreferences'] as const,
    bookmarks: () => [...queryKeys.userPreferences.all, 'bookmarks'] as const,
  },

  walkieTalkie: {
    all: ['walkie-talkie'] as const,
    roster: (clientInstanceId?: string, accountId?: string | null, membershipId?: string | null) =>
      [...queryKeys.walkieTalkie.all, 'roster', clientInstanceId ?? null, accountId ?? null, membershipId ?? null] as const,
  },

  // Stores
  stores: {
    all: ['stores'] as const,
    list: (accountId?: string | null) => (
      accountId
        ? [...queryKeys.stores.all, 'list', accountId] as const
        : [...queryKeys.stores.all, 'list'] as const
    ),
    detail: (id: string) => [...queryKeys.stores.all, id] as const,
  },

  // Products
  products: {
    all: (storeId: string) => ['products', storeId] as const,
    list: (storeId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.products.all(storeId), 'list', filters] as const,
    detail: (storeId: string, id: string) =>
      [...queryKeys.products.all(storeId), id] as const,
  },

  labels: {
    all: (storeId: string) => ['labels', storeId] as const,
    shipmentPlanStatus: (storeId: string, shipmentPlanId: string) =>
      [...queryKeys.labels.all(storeId), 'shipment-plan-status', shipmentPlanId] as const,
  },

  // Suppliers
  suppliers: {
    all: (storeId: string) => ['suppliers', storeId] as const,
    list: (storeId: string) => [...queryKeys.suppliers.all(storeId), 'list'] as const,
    detail: (storeId: string, id: string) =>
      [...queryKeys.suppliers.all(storeId), id] as const,
  },

  // Inventory
  inventory: {
    all: (storeId: string) => ['inventory', storeId] as const,
    list: (storeId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.inventory.all(storeId), 'list', filters] as const,
    audit: (storeId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.inventory.all(storeId), 'audit', filters] as const,
    auditOptions: (storeId: string) =>
      [...queryKeys.inventory.all(storeId), 'audit-options'] as const,
    restockSuggestions: (storeId: string) =>
      [...queryKeys.inventory.all(storeId), 'restock-suggestions'] as const,
  },

  // Restock
  restock: {
    all: (storeId: string) => ['restock', storeId] as const,
    list: (storeId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.restock.all(storeId), 'list', filters] as const,
    table: (
      storeId: string,
      params: {
        page: number;
        limit: number;
        orderQuantities?: { productId: string; value: number | null }[];
        sortBy: string;
        sortOrder: 'asc' | 'desc';
        filters: unknown;
      }
    ) => [...queryKeys.restock.all(storeId), 'table', params] as const,
  },

  // Purchase Orders
  purchaseOrders: {
    all: (storeId: string) => ['purchase-orders', storeId] as const,
    list: (storeId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.purchaseOrders.all(storeId), 'list', filters] as const,
    detail: (storeId: string, id: string) =>
      [...queryKeys.purchaseOrders.all(storeId), id] as const,
  },

  // Shipment Plans
  shipmentPlans: {
    all: (storeId: string) => ['shipment-plans', storeId] as const,
    list: (storeId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.shipmentPlans.all(storeId), 'list', filters] as const,
    detail: (storeId: string, id: string) =>
      [...queryKeys.shipmentPlans.all(storeId), id] as const,
  },

  // Shipments
  shipments: {
    all: (storeId: string) => ['shipments', storeId] as const,
    list: (storeId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.shipments.all(storeId), 'list', filters] as const,
    detail: (storeId: string, id: string) =>
      [...queryKeys.shipments.all(storeId), id] as const,
  },

  // Listings
  listings: {
    all: (storeId: string) => ['listings', storeId] as const,
    list: (storeId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.listings.all(storeId), 'list', filters] as const,
    detail: (storeId: string, id: string) =>
      [...queryKeys.listings.all(storeId), id] as const,
  },

  // Dashboard
  dashboard: {
    all: (storeId: string) => ['dashboard', storeId] as const,
    kpis: (storeId: string, period: '7d' | '30d') =>
      [...queryKeys.dashboard.all(storeId), 'kpis', period] as const,
    insights: (storeId: string) =>
      [...queryKeys.dashboard.all(storeId), 'insights'] as const,
    topProducts: (storeId: string, period?: number) =>
      [...queryKeys.dashboard.all(storeId), 'top-products', period] as const,
    supplierRestock: (storeId: string) =>
      [...queryKeys.dashboard.all(storeId), 'supplier-restock'] as const,
  },

  // Sync (store ID is passed via header, but we include it in key for cache separation)
  sync: {
    all: () => ['sync'] as const,
    status: () => [...queryKeys.sync.all(), 'status'] as const,
    logs: () => [...queryKeys.sync.all(), 'logs'] as const,
  },

  // Alerts
  alerts: {
    all: (storeId: string) => ['alerts', storeId] as const,
    list: (storeId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.alerts.all(storeId), 'list', filters] as const,
  },

  // Workflow Builder
  workflow: {
    all: (storeId: string) => ['workflow', storeId] as const,
    eligiblePOs: (storeId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.workflow.all(storeId), 'eligible-pos', filters] as const,
    poItems: (storeId: string, poId: string) =>
      [...queryKeys.workflow.all(storeId), 'po-items', poId] as const,
    draftShipmentPlans: (storeId: string) =>
      [...queryKeys.workflow.all(storeId), 'draft-shipment-plans'] as const,
    shipmentPlanDetail: (storeId: string, planId: string) =>
      [...queryKeys.workflow.all(storeId), 'shipment-plan', planId] as const,
    availableProductsRoot: (storeId: string) =>
      [...queryKeys.workflow.all(storeId), 'available-products'] as const,
    availableProducts: (storeId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.workflow.all(storeId), 'available-products', filters] as const,
  },

  // Ship-From Addresses
  shipFromAddresses: {
    all: (storeId: string) => ['ship-from-addresses', storeId] as const,
    list: (storeId: string) =>
      [...queryKeys.shipFromAddresses.all(storeId), 'list'] as const,
    detail: (storeId: string, id: string) =>
      [...queryKeys.shipFromAddresses.all(storeId), id] as const,
    default: (storeId: string) =>
      [...queryKeys.shipFromAddresses.all(storeId), 'default'] as const,
  },

  // Draft Shipments
  drafts: {
    all: (storeId: string) => ['drafts', storeId] as const,
    counts: (storeId: string) =>
      [...queryKeys.drafts.all(storeId), 'counts'] as const,
    list: (storeId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.drafts.all(storeId), 'list', filters] as const,
    detail: (storeId: string, id: string) =>
      [...queryKeys.drafts.all(storeId), id] as const,
  },

  // AI Features
  ai: {
    all: (storeId: string) => ['ai', storeId] as const,
    status: (storeId: string) =>
      [...queryKeys.ai.all(storeId), 'status'] as const,
    portfolioInsights: (storeId: string) =>
      [...queryKeys.ai.all(storeId), 'portfolio-insights'] as const,
    productInsight: (storeId: string, productId: string) =>
      [...queryKeys.ai.all(storeId), 'product-insight', productId] as const,
    conversations: (storeId: string) =>
      [...queryKeys.ai.all(storeId), 'conversations'] as const,
    conversation: (storeId: string, conversationId: string) =>
      [...queryKeys.ai.all(storeId), 'conversation', conversationId] as const,
    supplierMappingReview: (
      storeId: string,
      filters?: Record<string, unknown>
    ) => (
      filters
        ? [...queryKeys.ai.all(storeId), 'supplier-mapping-review', filters] as const
        : [...queryKeys.ai.all(storeId), 'supplier-mapping-review'] as const
    ),
  },

  // Solicitations
  solicitations: {
    all: (storeId: string) => ['solicitations', storeId] as const,
    settings: (storeId: string) =>
      [...queryKeys.solicitations.all(storeId), 'settings'] as const,
    campaigns: (storeId: string) =>
      [...queryKeys.solicitations.all(storeId), 'campaigns'] as const,
    log: (storeId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.solicitations.all(storeId), 'log', filters] as const,
    backlogCount: (storeId: string) =>
      [...queryKeys.solicitations.all(storeId), 'backlog-count'] as const,
    analytics: (storeId: string, type: string, params?: Record<string, unknown>) =>
      [...queryKeys.solicitations.all(storeId), 'analytics', type, params] as const,
  },

  // Listing Alerts
  listingAlerts: {
    all: (storeId: string) => ['listing-alerts', storeId] as const,
    list: (storeId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.listingAlerts.all(storeId), 'list', filters] as const,
    unreadCount: (storeId: string) =>
      [...queryKeys.listingAlerts.all(storeId), 'unread-count'] as const,
  },

  // Reviews
  reviews: {
    all: (storeId: string) => ['reviews', storeId] as const,
    snapshots: (storeId: string, asin: string) =>
      [...queryKeys.reviews.all(storeId), 'snapshots', asin] as const,
    alerts: (storeId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.reviews.all(storeId), 'alerts', filters] as const,
  },

  // Finalization Workflow
  finalization: {
    all: (storeId: string) => ['finalization', storeId] as const,
    state: (storeId: string, shipmentId: string) =>
      [...queryKeys.finalization.all(storeId), 'state', shipmentId] as const,
    placementOptions: (storeId: string, shipmentId: string) =>
      [...queryKeys.finalization.all(storeId), 'placement', shipmentId] as const,
    transportationOptions: (storeId: string, shipmentId: string) =>
      [...queryKeys.finalization.all(storeId), 'transportation', shipmentId] as const,
    deliveryWindows: (storeId: string, shipmentId: string) =>
      [...queryKeys.finalization.all(storeId), 'delivery-windows', shipmentId] as const,
    tracking: (storeId: string, shipmentId: string) =>
      [...queryKeys.finalization.all(storeId), 'tracking', shipmentId] as const,
    carrierOptions: (storeId: string, shipmentId: string) =>
      [...queryKeys.finalization.all(storeId), 'carrier-options', shipmentId] as const,
    planSummary: (storeId: string, planId: string) =>
      [...queryKeys.finalization.all(storeId), 'plan-summary', planId] as const,
    planTransportTotals: (storeId: string, planId: string) =>
      [...queryKeys.finalization.all(storeId), 'plan-transport-totals', planId] as const,
  },
};
